/**
 * useMediaQuery hook — replacement for MUI's useMediaQuery().
 *
 * Uses window.matchMedia() directly, no MUI dependency.
 */

import { useState, useSyncExternalStore } from 'react'

const hasMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'

interface MediaQueryStore {
  query: string
  list: MediaQueryList | null
  subscribe: (callback: () => void) => () => void
  getSnapshot: () => boolean
}

const createStore = (query: string): MediaQueryStore => {
  const list = hasMatchMedia() ? window.matchMedia(query) : null
  return {
    query,
    list,
    subscribe: callback => {
      list?.addEventListener('change', callback)
      return () => list?.removeEventListener('change', callback)
    },
    getSnapshot: () => list?.matches ?? false,
  }
}

const getServerSnapshot = () => false

/**
 * Subscribe to a media query and return whether it matches.
 *
 * One `MediaQueryList` per mounted hook, made once, and a stable subscription (GH #3110 follow-up). It used to call
 * `window.matchMedia` on every render, about four times, and subscribe again each time. That is a media query
 * evaluation, which can force a style recalculation, in every render of every component that asks. A component that
 * re-renders on each keystroke (a document title as its entity changes) made typing measurably slower.
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 599px)')
 * const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
 */
export const useMediaQuery = (query: string): boolean => {
  const [store, setStore] = useState(() => createStore(query))
  /** A new query string is a new list (queries are constants in practice; this keeps the hook correct if not) */
  const current = store.query === query ? store : createStore(query)
  if (current !== store) {
    setStore(current)
  }
  return useSyncExternalStore(current.subscribe, current.getSnapshot, getServerSnapshot)
}
